import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { isDev } from 'src/config';
import { getAccessToken } from 'src/utils/tokenService';
import { useAssessmentsScenarioNamesQuery, useControlsQuery } from 'src/graphql/_generated';

// Scenario page matches /risk-assessments/:assessmentId/scenario/:scenarioId
// (see pages/SignedInPage/index.tsx). If the user triggers the debug panel
// from that page we preselect the scenario for them — one less click.
const SCENARIO_PATH_RE = /^\/risk-assessments\/([^/]+)\/scenario\/([^/]+)/;
// Assessment-only page (no scenario suffix). Same auto-preselect — opens
// the panel on the assessment view, picks `assessment` type with the
// assessment id filled in.
const ASSESSMENT_PATH_RE = /^\/risk-assessments\/([^/]+)$/;

const EXPORT_RUNNER_URL = 'http://localhost:4000';

// Doc-app trigger is fire-and-forget — the spawned job notifies the API on
// completion via its own callback, so this panel only tracks whether the
// signal was accepted (`sent`) or failed to leave the browser (`error`).
type Status = 'idle' | 'sent' | 'error';
type Format = 'pdf' | 'pptx' | 'docx';
type DocType = 'test' | 'rmp' | 'scenario' | 'assessment' | 'insights';
type Route = '/run' | '/run/docker';
type CompletionStatus = 'ALL' | 'COMPLETED' | 'INCOMPLETED';

// Mirrors export-runner's DocConfigSchema discriminated union. The server-side Zod
// schema validates this on every request; this local type is for FE ergonomics.
type DocConfigPayload =
  | {
      // Stress-test deck. Schema-locked to pdf; `pageCount` controls how
      // many duplicated pages get stamped (each: text + placeholder image).
      type: 'test';
      format: 'pdf';
      pageCount: number;
    }
  | {
      type: 'rmp';
      format: Format;
      controlIds: string[];
      includesActions: { enabled: boolean; completionStatus: CompletionStatus };
    }
  | {
      type: 'scenario';
      format: Format;
      scenarioId: string;
      // Derived from the selected scenario's parent assessment — the picker
      // is a scenario-only dropdown, so there's never a case where the two
      // are picked independently.
      assessmentId: string;
      isLogarithmic: boolean;
      includesRMP: boolean;
      includesIncidents: boolean;
      includesActions: { enabled: boolean; completionStatus: CompletionStatus };
    }
  | {
      type: 'assessment';
      format: Format;
      assessmentId: string;
      // Empty array = include every scenario in the assessment (mirrors
      // FE PdfAssessment's `renderScenarios = []` convention).
      renderScenarios: string[];
      isLogarithmic: boolean;
      includesExecSummary: boolean;
      includesScenarios: boolean;
      includesRMP: boolean;
      includesIncidents: boolean;
      includesRiskTolerance: boolean;
      includesActions: { enabled: boolean; completionStatus: CompletionStatus };
    }
  | {
      // Insights deck — landscape dashboard summary. Server-side schema
      // (InsightsConfigSchema) locks `format` to 'pdf' only; the FE picker
      // hides format selection when this type is chosen.
      type: 'insights';
      format: 'pdf';
      assessmentId: string;
    };

const Panel = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 99999;
  width: 400px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-family: 'Poppins', 'Inter', system-ui, sans-serif;
  font-size: 13px;
  color: #111827;
  box-shadow:
    0 12px 32px rgba(15, 23, 42, 0.18),
    0 2px 6px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #1f2937;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  user-select: none;
`;

const HeaderRight = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleChevron = styled.span<{ $minimised: boolean }>`
  display: inline-block;
  width: 14px;
  text-align: center;
  font-size: 11px;
  opacity: 0.75;
  transform: rotate(${({ $minimised }) => ($minimised ? '-90deg' : '0deg')});
  transition: transform 120ms ease;
`;

const HeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const HeaderDot = styled.span<{ $status: Status }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status }) => ($status === 'sent' ? '#22c55e' : $status === 'error' ? '#ef4444' : '#9ca3af')};
`;

const HeaderMeta = styled.span`
  font-size: 11px;
  opacity: 0.7;
  font-weight: 400;
`;

const Body = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.span`
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 10px;
  font-weight: 600;
  min-width: 60px;
`;

const Select = styled.select`
  flex: 1;
  background: #ffffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 7px 10px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const NumberInput = styled.input`
  flex: 1;
  background: #ffffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 7px 10px;
  font-family: inherit;
  font-size: 13px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 2px;
  background: #f3f4f6;
  padding: 3px;
  border-radius: 6px;
  flex: 1;
`;

const ToggleButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  background: ${({ $selected }) => ($selected ? '#ffffff' : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#111827' : '#6b7280')};
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 2px rgba(15, 23, 42, 0.1)' : 'none')};
  transition: background 120ms ease;
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $variant: 'node' | 'docker' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${({ $variant }) => ($variant === 'docker' ? '#2563eb' : '#059669')};
  color: #ffffff;
  border: none;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  transition:
    background 120ms ease,
    transform 80ms ease;
  &:hover {
    background: ${({ $variant }) => ($variant === 'docker' ? '#1d4ed8' : '#047857')};
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    background: #9ca3af;
    cursor: default;
    transform: none;
  }
`;

const StatusBar = styled.div<{ $status: Status }>`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  background: ${({ $status }) => ($status === 'sent' ? '#ecfdf5' : $status === 'error' ? '#fef2f2' : '#f9fafb')};
  color: ${({ $status }) => ($status === 'sent' ? '#065f46' : $status === 'error' ? '#991b1b' : '#4b5563')};
  border: 1px solid ${({ $status }) => ($status === 'sent' ? '#a7f3d0' : $status === 'error' ? '#fecaca' : '#e5e7eb')};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`;

const SectionLabel = styled.div`
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 10px;
  font-weight: 600;
`;

const Checklist = styled.div`
  max-height: 140px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 0;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 12px;
  &:hover {
    background: #f3f4f6;
  }
`;

const CheckInput = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const InlineLabel = styled.span`
  font-size: 12px;
  color: #374151;
`;

const MetaNote = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

// Shared "Include actions" + status toggle. Used by both the rmp and
// scenario sections — both templates carry the same `includesActions`
// payload shape, so the UI is identical in both places. Extracted here so
// the two call sites don't drift.
const IncludeActions: React.FC<{
  actionsEnabled: boolean;
  setActionsEnabled: (v: boolean) => void;
  completionStatus: CompletionStatus;
  setCompletionStatus: (v: CompletionStatus) => void;
  disabled: boolean;
}> = ({ actionsEnabled, setActionsEnabled, completionStatus, setCompletionStatus, disabled }) => (
  <>
    <InlineRow>
      <SectionLabel>Include actions</SectionLabel>
      <CheckInput checked={actionsEnabled} onChange={(e) => setActionsEnabled(e.target.checked)} disabled={disabled} />
    </InlineRow>
    {actionsEnabled && (
      <Row>
        <Label>Status</Label>
        <ToggleGroup>
          <ToggleButton
            $selected={completionStatus === 'ALL'}
            onClick={() => setCompletionStatus('ALL')}
            disabled={disabled}
          >
            All
          </ToggleButton>
          <ToggleButton
            $selected={completionStatus === 'COMPLETED'}
            onClick={() => setCompletionStatus('COMPLETED')}
            disabled={disabled}
          >
            Done
          </ToggleButton>
          <ToggleButton
            $selected={completionStatus === 'INCOMPLETED'}
            onClick={() => setCompletionStatus('INCOMPLETED')}
            disabled={disabled}
          >
            Open
          </ToggleButton>
        </ToggleGroup>
      </Row>
    )}
  </>
);

const ExportRunnerDebugButton: React.FC = () => {
  // Dev-only: zero footprint in production bundles (component short-circuits
  // to null before any state or children mount).
  if (!isDev) return null;

  const [type, setType] = useState<DocType>('test');
  const [format, setFormat] = useState<Format>('pdf');
  // `test`-template-specific: how many duplicated pages to stamp. No
  // upper bound — the schema only enforces `>= 1`, so the renderer can be
  // pushed as hard as the operator wants.
  const [testPageCount, setTestPageCount] = useState<number>(1);
  const [status, setStatus] = useState<Status>('idle');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [lastMode, setLastMode] = useState<string | null>(null);
  const [lastReportId, setLastReportId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  // Persisted across reloads so the panel doesn't pop back open on every
  // page nav once a dev has tucked it away. Lazy initialiser so the
  // localStorage read happens once and tolerates SSR / disabled storage.
  const [minimised, setMinimised] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('exportRunnerDebug:minimised') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('exportRunnerDebug:minimised', minimised ? '1' : '0');
    } catch {
      // no-op — private mode / blocked storage shouldn't break the dev panel
    }
  }, [minimised]);

  // rmp-specific state. Kept outside the discriminated payload so the user
  // doesn't lose selections when flipping type back and forth.
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([]);
  const [actionsEnabled, setActionsEnabled] = useState<boolean>(true);
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus>('ALL');

  // scenario-specific state. Same "keep across type switches" rationale.
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  // Matches the Risk App's default — ScenarioPage/index.tsx initialises its
  // `isLogarithmic` state to `true`, so a user on a scenario page who hits
  // "Export PDF" gets a log chart. We mirror that default so the server
  // export looks the same as the client export without extra clicks.
  const [isLogarithmic, setIsLogarithmic] = useState<boolean>(true);
  const [includesRMP, setIncludesRMP] = useState<boolean>(true);
  const [includesIncidents, setIncludesIncidents] = useState<boolean>(true);

  // assessment-specific state. Defaults mirror the FE PdfAssessment "Export
  // PDF" modal — every section on, risk tolerance off, no scenario filter.
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>('');
  const [renderScenarioIds, setRenderScenarioIds] = useState<string[]>([]);
  const [includesExecSummary, setIncludesExecSummary] = useState<boolean>(true);
  const [includesScenarios, setIncludesScenarios] = useState<boolean>(true);
  const [includesRiskTolerance, setIncludesRiskTolerance] = useState<boolean>(false);

  // If the user opens the panel while on a scenario page, flip the type to
  // `scenario` and preselect that scenario. Only runs while the panel state
  // is still idle (type=test + no scenario picked) so we never clobber a
  // selection the user has actively made. Triggers on route change too — if
  // they navigate to a different scenario, the idle panel follows.
  const { pathname } = useLocation();
  const routeScenarioId = useMemo(() => {
    const match = pathname.match(SCENARIO_PATH_RE);
    return match ? match[2] : null;
  }, [pathname]);
  const routeAssessmentId = useMemo(() => {
    const match = pathname.match(ASSESSMENT_PATH_RE);
    return match ? match[1] : null;
  }, [pathname]);

  useEffect(() => {
    if (!routeScenarioId) return;
    if (type !== 'test' || selectedScenarioId) return;
    setType('scenario');
    setSelectedScenarioId(routeScenarioId);
  }, [routeScenarioId, type, selectedScenarioId]);

  useEffect(() => {
    if (!routeAssessmentId) return;
    if (type !== 'test' || selectedAssessmentId) return;
    setType('assessment');
    setSelectedAssessmentId(routeAssessmentId);
  }, [routeAssessmentId, type, selectedAssessmentId]);

  // Only fetch when rmp is picked — no reason to hit the API otherwise.
  const { data: controlsData, loading: controlsLoading } = useControlsQuery({
    skip: type !== 'rmp',
  });
  const controls = useMemo(
    () => controlsData?.controls?.map((c) => ({ id: c.id, name: c.name })) ?? [],
    [controlsData],
  );

  // Lightweight picker data for the scenario type — one id+name per scenario,
  // grouped under its parent assessment so we can show the assessment name in
  // the dropdown and auto-derive assessmentId when the user picks. Also
  // powers the assessment-type picker (assessments + their scenario lists
  // for the optional renderScenarios filter).
  const { data: scenariosData, loading: scenariosLoading } = useAssessmentsScenarioNamesQuery({
    skip: type !== 'scenario' && type !== 'assessment' && type !== 'insights',
  });
  const scenarioOptions = useMemo(
    () =>
      (scenariosData?.assessments ?? []).flatMap((a) =>
        a.scenarios.map((s) => ({
          scenarioId: s.id,
          scenarioName: s.name ?? '(unnamed scenario)',
          assessmentId: a.id,
          assessmentName: a.name,
        })),
      ),
    [scenariosData],
  );
  const selectedScenario = scenarioOptions.find((o) => o.scenarioId === selectedScenarioId);

  // Assessment picker — one entry per assessment, with its scenario list
  // available for the optional `renderScenarios` filter.
  const assessmentOptions = useMemo(
    () =>
      (scenariosData?.assessments ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        scenarios: a.scenarios.map((s) => ({
          id: s.id,
          name: s.name ?? '(unnamed scenario)',
        })),
      })),
    [scenariosData],
  );
  const selectedAssessment = assessmentOptions.find((a) => a.id === selectedAssessmentId);

  // Reset the renderScenarios filter when the active assessment changes —
  // ids from one assessment never apply to another, and a stale set would
  // silently filter out everything.
  useEffect(() => {
    setRenderScenarioIds([]);
  }, [selectedAssessmentId]);

  const allScenariosSelected =
    !!selectedAssessment &&
    selectedAssessment.scenarios.length > 0 &&
    renderScenarioIds.length === selectedAssessment.scenarios.length;
  const toggleAllScenarios = () =>
    setRenderScenarioIds(allScenariosSelected ? [] : (selectedAssessment?.scenarios ?? []).map((s) => s.id));
  const toggleScenarioId = (id: string) =>
    setRenderScenarioIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allSelected = controls.length > 0 && selectedControlIds.length === controls.length;
  const toggleAll = () => setSelectedControlIds(allSelected ? [] : controls.map((c) => c.id));
  const toggleOne = (id: string) =>
    setSelectedControlIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const buildDocConfig = (): DocConfigPayload | { error: string } => {
    if (type === 'test') return { type: 'test', format: 'pdf', pageCount: testPageCount };
    if (type === 'scenario') {
      if (!selectedScenario) return { error: 'Pick a scenario.' };
      return {
        type: 'scenario',
        format,
        scenarioId: selectedScenario.scenarioId,
        assessmentId: selectedScenario.assessmentId,
        isLogarithmic,
        includesRMP,
        includesIncidents,
        includesActions: { enabled: actionsEnabled, completionStatus },
      };
    }
    if (type === 'assessment') {
      if (!selectedAssessment) return { error: 'Pick an assessment.' };
      return {
        type: 'assessment',
        format,
        assessmentId: selectedAssessment.id,
        renderScenarios: renderScenarioIds,
        isLogarithmic,
        includesExecSummary,
        includesScenarios,
        includesRMP,
        includesIncidents,
        includesRiskTolerance,
        includesActions: { enabled: actionsEnabled, completionStatus },
      };
    }
    if (type === 'insights') {
      if (!selectedAssessment) return { error: 'Pick an assessment.' };
      // Format locked to 'pdf' server-side (InsightsConfigSchema). The
      // picker keeps the existing `format` toggle visible but the payload
      // always sends 'pdf' regardless of the toggle's state.
      return {
        type: 'insights',
        format: 'pdf',
        assessmentId: selectedAssessment.id,
      };
    }
    if (selectedControlIds.length === 0) return { error: 'Pick at least one control.' };
    return {
      type: 'rmp',
      format,
      controlIds: selectedControlIds,
      includesActions: { enabled: actionsEnabled, completionStatus },
    };
  };

  const run = async (route: Route) => {
    const mode = route === '/run/docker' ? 'docker' : 'node';
    const docConfig = buildDocConfig();
    if ('error' in docConfig) {
      setStatus('error');
      setLastMode(mode);
      setLastError(docConfig.error);
      return;
    }

    setSubmitting(true);
    setLastMode(mode);
    setLastError(null);

    // Local-only label for forming s3Key + localOutputPathS3Override and a
    // placeholder display value while the request is in flight. The real
    // reportId is minted server-side (dev server in dev, BE in prod) and
    // returned in the response — see setLastReportId(body.reportId) below.
    const localId = `dev-${Date.now()}`;
    setLastReportId(localId);

    try {
      const authToken = await getAccessToken();
      if (!authToken) throw new Error('No access token available');

      // New camelCase contract — payload mirrors the JSON body the BE's
      // future GraphQL mutation will return when the export-runner boots in
      // prod, so the dev server (which simulates that mutation over REST)
      // can store it verbatim and hand it back unchanged.
      //
      // No reportId here: the server mints it. The export-runner only ever sees
      // reportId in the fetched config response, never the spawn request.
      //
      // exportAuthMode: 'user-jwt' opts the export-runner into the
      // Authorization-bearer transport for the forwarded FE Auth0 JWT
      // (`authToken` below). Default 'job-token' would expect a real
      // BE-issued per-job token instead, which we don't have in dev.
      const payload = {
        graphqlEndpoint: `${process.env.BASE_URL}api/public/api/graphql`,
        exportAuthMode: 'user-jwt',
        authToken,
        frontendUrl: window.location.origin,
        s3Bucket: 'cydea-risk-app-user-images',
        awsRegion: 'eu-west-2',
        // s3Key is required even in dev — keeps the shape uniform with
        // prod. The actual upload is skipped because of the override below.
        s3Key: `docs/${localId}.${format}`,
        // Dev-only override of the S3 destination — presence routes the
        // renderer to disk. Drop this field to drive the full S3 path
        // from a workstation.
        localOutputPathS3Override: `./output/${localId}.${format}`,
        docConfig,
      };

      console.log(`[export-runner debug] POST ${EXPORT_RUNNER_URL}${route}`, payload);

      const res = await fetch(`${EXPORT_RUNNER_URL}${route}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        reportId?: string;
        error?: string;
      };

      if (!res.ok) {
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      // Replace the local placeholder with the server-minted reportId so
      // the status line + dev-server console logs match.
      if (body.reportId) setLastReportId(body.reportId);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setLastError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const statusLine = (() => {
    if (status === 'idle') return 'Ready — pick params, then fire a runner.';
    if (status === 'sent') {
      return `→ ${lastMode} spawned${lastReportId ? ` — ${lastReportId}` : ''} (job runs async; check the export-runner server console)`;
    }
    return `✗ ${lastMode ?? 'run'} send failed — ${lastError ?? 'unknown error'}`;
  })();

  const disabled = submitting;

  return (
    <Panel>
      <Header onClick={() => setMinimised((m) => !m)} title={minimised ? 'Expand debug panel' : 'Collapse debug panel'}>
        <HeaderTitle>
          <HeaderDot $status={status} />
          export-runner debug
        </HeaderTitle>
        <HeaderRight>
          <HeaderMeta>localhost:4000</HeaderMeta>
          <ToggleChevron $minimised={minimised} aria-hidden>
            ▾
          </ToggleChevron>
        </HeaderRight>
      </Header>
      {!minimised && (
        <Body>
          <Row>
            <Label>Type</Label>
            <Select value={type} onChange={(e) => setType(e.target.value as DocType)} disabled={disabled}>
              <option value="test">test</option>
              <option value="rmp">rmp</option>
              <option value="scenario">scenario</option>
              <option value="assessment">assessment</option>
              <option value="insights">insights</option>
            </Select>
          </Row>
          <Row>
            <Label>Format</Label>
            <ToggleGroup>
              <ToggleButton $selected={format === 'pdf'} onClick={() => setFormat('pdf')} disabled={disabled}>
                PDF
              </ToggleButton>
              <ToggleButton $selected={format === 'pptx'} onClick={() => setFormat('pptx')} disabled={disabled}>
                PPTX
              </ToggleButton>
              <ToggleButton $selected={format === 'docx'} onClick={() => setFormat('docx')} disabled={disabled}>
                DOCX
              </ToggleButton>
            </ToggleGroup>
          </Row>

          {type === 'test' && (
            <Section>
              <InlineRow>
                <SectionLabel>Page count</SectionLabel>
                <MetaNote>{testPageCount === 1 ? '1 page' : `${testPageCount} pages`}</MetaNote>
              </InlineRow>
              <Row>
                <Label>Pages</Label>
                <NumberInput
                  type="number"
                  min={1}
                  step={1}
                  value={testPageCount}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    // Floor at 1 (matches schema); no upper bound — the
                    // template is meant to be pushed.
                    setTestPageCount(Math.max(1, Math.trunc(next)));
                  }}
                  disabled={disabled}
                />
              </Row>
              <Row>
                <Label>Presets</Label>
                <ToggleGroup>
                  {[1, 10, 50, 100, 500, 1000].map((n) => (
                    <ToggleButton
                      key={n}
                      $selected={testPageCount === n}
                      onClick={() => setTestPageCount(n)}
                      disabled={disabled}
                    >
                      {n}
                    </ToggleButton>
                  ))}
                </ToggleGroup>
              </Row>
              <MetaNote>PDF only. Format toggle is ignored for this template.</MetaNote>
            </Section>
          )}

          {type === 'rmp' && (
            <Section>
              <InlineRow>
                <SectionLabel>Controls</SectionLabel>
                <MetaNote>
                  {controlsLoading ? 'loading…' : `${selectedControlIds.length} / ${controls.length} selected`}
                </MetaNote>
              </InlineRow>
              <Checklist>
                {!controlsLoading && controls.length === 0 && (
                  <CheckRow as="div">
                    <MetaNote>No controls in this workspace.</MetaNote>
                  </CheckRow>
                )}
                {controls.length > 0 && (
                  <CheckRow>
                    <CheckInput checked={allSelected} onChange={toggleAll} disabled={disabled} />
                    <InlineLabel>
                      <strong>All controls</strong>
                    </InlineLabel>
                  </CheckRow>
                )}
                {controls.map((c) => (
                  <CheckRow key={c.id}>
                    <CheckInput
                      checked={selectedControlIds.includes(c.id)}
                      onChange={() => toggleOne(c.id)}
                      disabled={disabled}
                    />
                    <InlineLabel>{c.name}</InlineLabel>
                  </CheckRow>
                ))}
              </Checklist>

              <IncludeActions
                actionsEnabled={actionsEnabled}
                setActionsEnabled={setActionsEnabled}
                completionStatus={completionStatus}
                setCompletionStatus={setCompletionStatus}
                disabled={disabled}
              />
            </Section>
          )}

          {type === 'scenario' && (
            <Section>
              <InlineRow>
                <SectionLabel>Scenario</SectionLabel>
                <MetaNote>
                  {scenariosLoading
                    ? 'loading…'
                    : selectedScenario
                      ? selectedScenario.assessmentName
                      : `${scenarioOptions.length} available`}
                </MetaNote>
              </InlineRow>
              <Select
                value={selectedScenarioId}
                onChange={(e) => setSelectedScenarioId(e.target.value)}
                disabled={disabled || scenariosLoading}
              >
                <option value="">— pick a scenario —</option>
                {scenarioOptions.map((o) => (
                  <option key={o.scenarioId} value={o.scenarioId}>
                    {o.assessmentName} / {o.scenarioName}
                  </option>
                ))}
              </Select>

              <InlineRow>
                <InlineLabel>Logarithmic x-axis</InlineLabel>
                <CheckInput
                  checked={isLogarithmic}
                  onChange={(e) => setIsLogarithmic(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include RMP pages</InlineLabel>
                <CheckInput
                  checked={includesRMP}
                  onChange={(e) => setIncludesRMP(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include incident pages</InlineLabel>
                <CheckInput
                  checked={includesIncidents}
                  onChange={(e) => setIncludesIncidents(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>

              <IncludeActions
                actionsEnabled={actionsEnabled}
                setActionsEnabled={setActionsEnabled}
                completionStatus={completionStatus}
                setCompletionStatus={setCompletionStatus}
                disabled={disabled}
              />
            </Section>
          )}

          {type === 'assessment' && (
            <Section>
              <InlineRow>
                <SectionLabel>Assessment</SectionLabel>
                <MetaNote>
                  {scenariosLoading
                    ? 'loading…'
                    : selectedAssessment
                      ? `${selectedAssessment.scenarios.length} scenarios`
                      : `${assessmentOptions.length} available`}
                </MetaNote>
              </InlineRow>
              <Select
                value={selectedAssessmentId}
                onChange={(e) => setSelectedAssessmentId(e.target.value)}
                disabled={disabled || scenariosLoading}
              >
                <option value="">— pick an assessment —</option>
                {assessmentOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>

              {/* Optional scenario filter — empty list is the same as "all". */}
              {selectedAssessment && selectedAssessment.scenarios.length > 0 && (
                <>
                  <InlineRow>
                    <SectionLabel>Render scenarios (optional)</SectionLabel>
                    <MetaNote>
                      {renderScenarioIds.length === 0
                        ? 'all'
                        : `${renderScenarioIds.length} / ${selectedAssessment.scenarios.length} selected`}
                    </MetaNote>
                  </InlineRow>
                  <Checklist>
                    <CheckRow>
                      <CheckInput checked={allScenariosSelected} onChange={toggleAllScenarios} disabled={disabled} />
                      <InlineLabel>
                        <strong>All scenarios</strong>
                      </InlineLabel>
                    </CheckRow>
                    {selectedAssessment.scenarios.map((s) => (
                      <CheckRow key={s.id}>
                        <CheckInput
                          checked={renderScenarioIds.includes(s.id)}
                          onChange={() => toggleScenarioId(s.id)}
                          disabled={disabled}
                        />
                        <InlineLabel>{s.name}</InlineLabel>
                      </CheckRow>
                    ))}
                  </Checklist>
                </>
              )}

              <InlineRow>
                <InlineLabel>Logarithmic x-axis</InlineLabel>
                <CheckInput
                  checked={isLogarithmic}
                  onChange={(e) => setIsLogarithmic(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include exec summary</InlineLabel>
                <CheckInput
                  checked={includesExecSummary}
                  onChange={(e) => setIncludesExecSummary(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include scenario pages</InlineLabel>
                <CheckInput
                  checked={includesScenarios}
                  onChange={(e) => setIncludesScenarios(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include RMP pages</InlineLabel>
                <CheckInput
                  checked={includesRMP}
                  onChange={(e) => setIncludesRMP(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include incident pages</InlineLabel>
                <CheckInput
                  checked={includesIncidents}
                  onChange={(e) => setIncludesIncidents(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>
              <InlineRow>
                <InlineLabel>Include risk tolerance</InlineLabel>
                <CheckInput
                  checked={includesRiskTolerance}
                  onChange={(e) => setIncludesRiskTolerance(e.target.checked)}
                  disabled={disabled}
                />
              </InlineRow>

              <IncludeActions
                actionsEnabled={actionsEnabled}
                setActionsEnabled={setActionsEnabled}
                completionStatus={completionStatus}
                setCompletionStatus={setCompletionStatus}
                disabled={disabled}
              />
            </Section>
          )}

          {type === 'insights' && (
            <Section>
              <InlineRow>
                <SectionLabel>Assessment</SectionLabel>
                <MetaNote>
                  {scenariosLoading
                    ? 'loading…'
                    : selectedAssessment
                      ? selectedAssessment.name
                      : `${assessmentOptions.length} available`}
                </MetaNote>
              </InlineRow>
              <Select
                value={selectedAssessmentId}
                onChange={(e) => setSelectedAssessmentId(e.target.value)}
                disabled={disabled || scenariosLoading}
              >
                <option value="">— pick an assessment —</option>
                {assessmentOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
              <MetaNote>Insights deck is PDF only and has no per-section gates. Pick an assessment and run.</MetaNote>
            </Section>
          )}

          <ButtonRow>
            <ActionButton $variant="node" disabled={disabled} onClick={() => run('/run')}>
              ▶ Node spawn
            </ActionButton>
            <ActionButton $variant="docker" disabled={disabled} onClick={() => run('/run/docker')}>
              🐳 Docker run
            </ActionButton>
          </ButtonRow>
          <StatusBar $status={status}>{statusLine}</StatusBar>
        </Body>
      )}
    </Panel>
  );
};

export default ExportRunnerDebugButton;
